import { FREE_SHIPPING_OPTION } from "@/domains/careplan/models/constants";
import type { CareplanCartDataModel, DraftCart } from "@/domains/careplan/models/types";
import { env } from "@/env";
import { Divider, Heading } from "@/ui";
import { formatAmountAsString } from "@/utils/money";
import { CareplanCartLineItem } from "./careplan-cart-line-item";

type CareplanCartSubscriptionSummaryCardProps = {
  cart: NonNullable<CareplanCartDataModel>;
  draftCart: DraftCart;
};

export const CareplanCartSubscriptionSummaryCard = ({
  cart,
  draftCart,
}: CareplanCartSubscriptionSummaryCardProps) => {
  const activeDraftCartItems = draftCart.items.filter((item) => {
    return item.quantity > 0;
  });

  if (!activeDraftCartItems.length) {
    return null;
  }

  return (
    <div className="flex flex-col gap-4 rounded-xl bg-canvas p-5 text-sm">
      <Heading level={4}>Subscription Summary</Heading>

      {activeDraftCartItems.map((item) => {
        const lineItemTotal =
          item.source === "subscription"
            ? item.price * item.quantity
            : Number(item.originalPriceAsString) * item.quantity;

        return (
          <CareplanCartLineItem key={item.id}>
            <p className="text-content-secondary">{`${item.shortName} x ${item.quantity}`}</p>

            <p className="font-medium">${formatAmountAsString(`${lineItemTotal}`)}</p>
          </CareplanCartLineItem>
        );
      })}

      <Divider.Line />

      <CareplanCartLineItem>
        <p className="text-content-secondary">Subtotal</p>
        <p className="font-medium">${cart.cost.checkoutChargeAmount.amountAsString}</p>
      </CareplanCartLineItem>

      <CareplanCartLineItem className="text-danger">
        <p>Subscription Discount</p>
        <p className="font-medium">- ${formatAmountAsString(`${cart.subscriptionDiscount}`)}</p>
      </CareplanCartLineItem>

      <CareplanCartLineItem className="text-danger">
        <p>Care Plan Discount</p>
        <p className="font-medium">- ${formatAmountAsString(`${cart.careplanDiscount}`)}</p>
      </CareplanCartLineItem>

      {cart.discounts.map((discount) => {
        if (discount.code === env.VITE_CAREPLAN_DISCOUNT_CODE) {
          return null;
        }

        return (
          <CareplanCartLineItem className="text-danger" key={discount.code}>
            <p>{discount.code}</p>
            <p className="font-medium">- ${formatAmountAsString(`${discount.total}`)}</p>
          </CareplanCartLineItem>
        );
      })}

      {cart.selectedShippingOption ? (
        <CareplanCartLineItem>
          <p>{cart.selectedShippingOption.title}</p>
          <p className="font-medium">
            {cart.selectedShippingOption.costAsString === FREE_SHIPPING_OPTION
              ? FREE_SHIPPING_OPTION
              : `$${cart.selectedShippingOption.costAsString}`}
          </p>
        </CareplanCartLineItem>
      ) : null}

      <CareplanCartLineItem>
        <p>Order total</p>
        <p className="font-medium">${cart.cost.total.amountAsString}/month</p>
      </CareplanCartLineItem>
    </div>
  );
};
